import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import qahoy from "views_shared/app/javascript/scripts/qahoy";
import qapi from "views_shared/app/javascript/api";

import Tag from "./profiles/Tag";

// This component is model-generic — it fetches by profile or by tagged object — so it cannot know
// which filtered index a page family wants its chips to reach. Callers that have one supply a
// resolver; the rest keep the destination they have always had.
function defaultHashtagUrlResolver(hashtag) {
  return `/hashtags/${hashtag.hashtag_slug}`;
}

function Hashtags({
  profileId = null,
  taggedId = null,
  taggedType = null,
  maxHashtags = 10,
  clickEventName = null,
  hashtagUrlResolver = defaultHashtagUrlResolver,
  className = "",
}) {
  const [hashtagTags, setHashtagTags] = useState([]);

  if (profileId && (taggedId || taggedType)) {
    throw new Error("Cannot load hashtags for both profile & tagged!");
  }

  let keyStem;
  if (profileId) {
    keyStem = `profile-${profileId}`;
  } else if (taggedId || taggedType) {
    keyStem = `tagged-${taggedType}-${taggedId}`;
  } else {
    throw new Error();
  }

  const transformAndUniqueHashtags = (jsonData) => {
    const transformed = jsonData.map((item) => ({
      hashtag_id: item.hashtag_id,
      hashtag_name: item.hashtag_name,
      hashtag_slug: item.hashtag_slug,
    }));

    const uniqueTransformed = transformed.filter(
      (item, index, self) => index === self.findIndex((otherItem) => otherItem.hashtag_id === item.hashtag_id),
    );

    return uniqueTransformed;
  };

  useEffect(() => {
    if (taggedId == null || taggedType == null) return;
    const fetchData = async () => {
      const response = await qapi.hashtags.getAllForTaggedForDisplay(taggedId, taggedType);
      if (response.data) {
        const hashtagTagsResponse = response.data;
        const transformed = transformAndUniqueHashtags(hashtagTagsResponse);
        setHashtagTags(transformed);
      } else if (response.error) {
        throw new Error(JSON.stringify(response.error));
      }
    };

    fetchData();
  }, [taggedId, taggedType]);

  useEffect(() => {
    if (profileId == null) return;
    const fetchData = async () => {
      const response = await qapi.hashtags.getAllForProfileIdForDisplay(profileId);
      if (response.data) {
        const hashtagTagsResponse = response.data;
        const transformed = transformAndUniqueHashtags(hashtagTagsResponse);
        setHashtagTags(transformed);
      } else if (response.error) {
        throw new Error(JSON.stringify(response.error));
      }
    };

    fetchData();
  }, [profileId]);

  if (!hashtagTags || hashtagTags.length === 0) return null;

  const trackClicks = () => {
    if (clickEventName) {
      qahoy.track(clickEventName);
    }
  };

  const renderHashtags = () =>
    hashtagTags.slice(0, maxHashtags).map((hashtag) => (
      <Tag
        href={hashtagUrlResolver(hashtag)}
        key={`${keyStem}-${hashtag.hashtag_id}`}
        onClick={trackClicks}
        className="mb-1 me-1"
      >
        {hashtag.hashtag_name}
      </Tag>
    ));

  // This component owns its own container rather than letting the caller wrap it, because only it
  // knows whether the read produced any chips — a caller-side wrapper would sit empty on every
  // subject with no tags, and while the read is still in flight on every subject at all.
  return <div className={className}>{renderHashtags()}</div>;
}

Hashtags.propTypes = {
  profileId: PropTypes.number,
  taggedId: PropTypes.number,
  taggedType: PropTypes.string,
  maxHashtags: PropTypes.number,
  clickEventName: PropTypes.string,
  hashtagUrlResolver: PropTypes.func,
  className: PropTypes.string,
};

export default Hashtags;
